import React from "react";
import { InputText } from "primereact/inputtext";
import RFG from "../../libs/inputs/RFG";
import * as ACTION from "../../services/actions";
import {
  Card,
  CardBody,
  Button,
  Container,
  Col,
  Row,
  Alert,
  Spinner
} from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { Check, Warning, Info } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import Footer from "./Footer";

export default function OTP() {
  // ==================== HOOKS & STATE INITIALIZATION ====================
  
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  // Redux state selectors
  const otpState = useSelector((state) => state?.otpsubmitState);
  const resendOtpState = useSelector((state) => state?.resedotpState);
  
  // Form management
  const { 
    handleSubmit, 
    control, 
    setError, 
    clearErrors,
    formState: { errors, isSubmitting } 
  } = useForm({
    defaultValues: { otp: "" },
    mode: "onChange",
  });
  
  // Component state
  const [timer, setTimer] = React.useState(60);
  const [resendDisabled, setResendDisabled] = React.useState(true);
  const [customerData, setCustomerData] = React.useState(null);
  const [verificationAttempts, setVerificationAttempts] = React.useState(0);
  const [isProcessing, setIsProcessing] = React.useState(false);
  const [isVerificationComplete, setIsVerificationComplete] = React.useState(false);
  
  const MAX_VERIFICATION_ATTEMPTS = 3;
  const TIMER_DURATION = 60;

  // ==================== AUTHENTICATION STATE VALIDATION ====================

  React.useEffect(() => {
    console.log("🔐 [OTP] Initializing OTP verification component");
    
    const validateAuthenticationState = () => {
      try {
        const tempData = localStorage.getItem("temp-data");
        console.log("📋 [OTP] Checking authentication state:", {
          hasTempData: !!tempData,
          tempDataType: typeof tempData,
          tempDataLength: tempData?.length
        });
        
        if (!tempData || tempData === "null" || tempData === "undefined") {
          console.warn("⚠️ [OTP] Invalid authentication state - no temp data");
          redirectToPhoneVerification("No temporary authentication data found");
          return;
        }

        const custData = JSON.parse(tempData);
        
        const requiredFields = ['customerID', 'cellNumber', 'customerName'];
        const missingFields = requiredFields.filter(field => !custData[field]);
        
        if (missingFields.length > 0) {
          console.error("❌ [OTP] Invalid customer data structure:", {
            missingFields,
            receivedData: custData
          });
          redirectToPhoneVerification("Invalid customer data structure");
          return;
        }
        
        setCustomerData(custData);
        console.log("✅ [OTP] Customer data validated and loaded:", {
          customerID: custData.customerID,
          customerName: custData.customerName,
          cellNumber: custData.cellNumber.replace(/(\d{6})(\d{4})/, "$1****")
        });
        
      } catch (error) {
        console.error("❌ [OTP] Error validating authentication state:", error);
        redirectToPhoneVerification("Authentication data parsing error");
      }
    };

    const redirectToPhoneVerification = (reason) => {
      console.log(`🔄 [OTP] Redirecting to phone verification: ${reason}`);
      localStorage.removeItem("temp-data");
      navigate("/", { 
        replace: true,
        state: { message: "Please start the authentication process again" }
      });
    };

    validateAuthenticationState();
  }, [navigate]);

  // ==================== TIMER MANAGEMENT ====================
  
  React.useEffect(() => {
    let intervalId;
    
    if (timer > 0) {
      intervalId = setInterval(() => {
        setTimer((prevTimer) => {
          const newTimer = prevTimer - 1;
          if (newTimer === 0) {
            console.log("⏰ [OTP] Timer expired, enabling resend");
            setResendDisabled(false);
          }
          return newTimer;
        });
      }, 1000);
    }
    
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [timer]);

  // ==================== UTILITY FUNCTIONS ====================
  
  const validateOTP = (otp) => {
    const otpPattern = /^[0-9]{4}$/;
    return otpPattern.test(otp.toString());
  };

  /**
   * Idempotent token storage - prevents double execution issues
   */
  const storeAuthenticationToken = (token) => {
    try {
      // Check if token is already stored to prevent double processing
      const existingToken = localStorage.getItem("authToken");
      if (existingToken === token) {
        console.log("🔑 [OTP] Token already stored, skipping duplicate storage");
        return;
      }

      const tokenData = {
        token,
        timestamp: Date.now(),
        expiresIn: 24 * 60 * 60 * 1000
      };
      
      localStorage.setItem("authToken", token);
      localStorage.setItem("tokenMetadata", JSON.stringify(tokenData));
      
      console.log("🔑 [OTP] Authentication token stored securely");
    } catch (error) {
      console.error("❌ [OTP] Error storing authentication token:", error);
      throw new Error("Failed to store authentication credentials");
    }
  };

  /**
   * Idempotent session finalization - handles double execution gracefully
   */
  const finalizeUserSession = () => {
    try {
      // Check if data is already in permanent storage
      const existingData = localStorage.getItem("data");
      const tempData = localStorage.getItem("temp-data");
      
      if (existingData && !tempData) {
        console.log("💾 [OTP] User session already finalized, skipping");
        return;
      }
      
      if (!tempData) {
        // Check if we have existing data (session already finalized)
        if (existingData) {
          console.log("💾 [OTP] Session already finalized with existing data");
          return;
        }
        throw new Error("No temporary user data found and no existing session");
      }
      
      // Move data from temporary to permanent storage
      localStorage.setItem("data", tempData);
      localStorage.setItem("sessionStartTime", Date.now().toString());
      localStorage.setItem("lastActivity", Date.now().toString());
      
      // Clear temporary data
      localStorage.removeItem("temp-data");
      
      console.log("💾 [OTP] User session finalized and data transferred");
    } catch (error) {
      console.error("❌ [OTP] Error finalizing user session:", error);
      throw new Error("Failed to finalize user session");
    }
  };

  // ==================== EVENT HANDLERS ====================
  
  const handleResendOTP = React.useCallback(() => {
    if (!customerData) {
      console.error("❌ [OTP] Cannot resend - no customer data");
      return;
    }
    
    console.log("📤 [OTP] Initiating OTP resend request");
    
    setTimer(TIMER_DURATION);
    setResendDisabled(true);
    
    dispatch({
      type: `${ACTION.RESEDOTP}_POST`,
      payload: { 
        phone: customerData.cellNumber,
        requestId: `resend_${Date.now()}`
      },
      callback: (resp) => {
        console.log("📨 [OTP] Resend response received:", {
          success: resp?.is_success,
          message: resp?.message
        });
        
        if (resp?.is_success) {
          console.log("✅ [OTP] OTP resent successfully");
        } else {
          console.error("❌ [OTP] Failed to resend OTP:", resp?.message);
          setTimer(0);
          setResendDisabled(false);
        }
      }
    });
  }, [customerData, dispatch]);

  /**
   * Added verification completion check to prevent double execution
   */
  const handleOTPSubmission = React.useCallback((formData) => {
    // Prevent double execution
    if (isVerificationComplete || isProcessing) {
      console.log("⚠️ [OTP] Verification already in progress or completed, skipping");
      return;
    }

    console.log("🔐 [OTP] Processing OTP verification request");
    
    if (!customerData) {
      console.error("❌ [OTP] Cannot verify - no customer data");
      return;
    }

    if (verificationAttempts >= MAX_VERIFICATION_ATTEMPTS) {
      console.error("❌ [OTP] Maximum verification attempts exceeded");
      setError("otp", {
        type: "manual",
        message: "Maximum verification attempts exceeded. Please request a new OTP."
      });
      return;
    }

    if (!validateOTP(formData.otp)) {
      console.error("❌ [OTP] Invalid OTP format");
      setError("otp", {
        type: "manual",
        message: "Please enter a valid 4-digit OTP"
      });
      return;
    }

    setIsProcessing(true);
    setVerificationAttempts(prev => prev + 1);
    
    console.log("📡 [OTP] Sending verification request to server:", {
      phone: customerData.cellNumber.replace(/(\d{6})(\d{4})/, "$1****"),
      attempt: verificationAttempts + 1,
      maxAttempts: MAX_VERIFICATION_ATTEMPTS
    });

    dispatch({
      type: `${ACTION.OTPSUBMIT}_POST`,
      payload: { 
        phone: customerData.cellNumber, 
        otp: formData.otp,
        deviceId: navigator.userAgent,
        timestamp: Date.now()
      },
      callback: (resp) => {
        console.log("📨 [OTP] Verification response received:", {
          success: resp?.is_success,
          hasToken: !!(resp?.data?.token),
          message: resp?.message
        });
        
        if (resp?.is_success && resp?.data?.token && !isVerificationComplete) {
          handleSuccessfulVerification(resp.data.token);
        } else if (!isVerificationComplete) {
          handleFailedVerification(resp);
        } else {
          console.log("⚠️ [OTP] Verification already completed, ignoring duplicate response");
        }
        
        setIsProcessing(false);
      }
    });
  }, [customerData, verificationAttempts, dispatch, setError, isVerificationComplete, isProcessing]);

  /**
   * Added completion flag and better error handling
   */
  const handleSuccessfulVerification = (token) => {
    // Prevent double execution
    if (isVerificationComplete) {
      console.log("⚠️ [OTP] Verification already completed, skipping duplicate success handler");
      return;
    }

    try {
      console.log("🎉 [OTP] OTP verification successful");
      setIsVerificationComplete(true); // Mark as complete immediately
      
      clearErrors("otp");
      
      // Store authentication credentials (idempotent)
      storeAuthenticationToken(token);
      
      // Finalize user session (idempotent)
      finalizeUserSession();
      
      console.log("🚀 [OTP] Redirecting to dashboard");
      
      // Navigate to dashboard
      navigate("/dashboard/", { 
        state: { 
          token,
          authenticated: true,
          loginTime: Date.now()
        },
        replace: true
      });
      
    } catch (error) {
      console.error("❌ [OTP] Error handling successful verification:", error);
      setIsVerificationComplete(false); // Reset on error
      setError("otp", {
        type: "manual",
        message: "Authentication successful but session setup failed. Please try again."
      });
    }
  };

  const handleFailedVerification = (response) => {
    console.log("❌ [OTP] OTP verification failed:", {
      attemptsRemaining: MAX_VERIFICATION_ATTEMPTS - verificationAttempts,
      message: response?.message
    });
    
    const remainingAttempts = MAX_VERIFICATION_ATTEMPTS - verificationAttempts;
    
    if (remainingAttempts > 0) {
      setError("otp", {
        type: "manual",
        message: `Invalid OTP. ${remainingAttempts} attempt${remainingAttempts > 1 ? 's' : ''} remaining.`
      });
    } else {
      setError("otp", {
        type: "manual",
        message: "Maximum attempts exceeded. Please request a new OTP."
      });
    }
  };

  // ==================== RENDER HELPERS ====================
  
  const renderCustomerInfo = () => {
    if (!customerData) return null;
    
    return (
      <Alert variant="info" className="mb-4">
        <div className="d-flex align-items-center">
          <Info size={20} className="me-2" />
          <div>
            <div className="fw-bold">Verification Details</div>
            <small>
              OTP sent to: <strong>{customerData.cellNumber.replace(/(\d{6})(\d{4})/, "$1****")}</strong><br/>
              Customer: <strong>{customerData.customerName}</strong><br/>
              ID: <strong>{customerData.customerID}</strong>
            </small>
          </div>
        </div>
      </Alert>
    );
  };

  const renderLoadingState = () => (
    <div className="text-center py-3">
      <Spinner animation="border" size="sm" className="me-2" />
      <span>Verifying OTP...</span>
    </div>
  );

  const renderErrorMessages = () => {
    const hasErrors = otpState?.error || resendOtpState?.error || errors.otp;
    
    if (!hasErrors) return null;
    
    return (
      <div className="mt-3">
        {errors.otp && (
          <Alert variant="danger" className="py-2">
            <Warning size={16} className="me-1" />
            {errors.otp.message}
          </Alert>
        )}
        
        {otpState?.error && (
          <Alert variant="danger" className="py-2">
            <Warning size={16} className="me-1" />
            Verification Error: {otpState.error}
          </Alert>
        )}
        
        {resendOtpState?.error && (
          <Alert variant="warning" className="py-2">
            <Warning size={16} className="me-1" />
            Resend Error: {resendOtpState.error}
          </Alert>
        )}
      </div>
    );
  };

  // ==================== COMPONENT RENDER ====================
  
  return (
    <>
      <Container fluid className="p-1 g-0" style={{ height: "100%" }}>
        <Row className="d-flex justify-content-center g-0">
          <Col xs={12} sm={8} md={5} xl={4}>
            <Card style={{ minWidth: 210, height: "auto", border: "none" }}>
              <CardBody className="px-4 mt-4">
                <h1 className="mb-4 app-title text-center">Payment Portal</h1>
                
                {renderCustomerInfo()}
                
                <form
                  autoComplete="off"
                  onSubmit={handleSubmit(handleOTPSubmission)}
                >
                  <RFG
                    name="otp"
                    errormessage="4-digit OTP is required"
                    label="Enter the 4-Digit OTP Received"
                    control={control}
                    rules={{ 
                      required: "OTP is required",
                      pattern: {
                        value: /^[0-9]{4}$/,
                        message: "OTP must be exactly 4 digits"
                      }
                    }}
                    className="mb-3"
                  >
                    <InputText 
                      autoComplete="off" 
                      placeholder="Enter 4-digit OTP"
                      maxLength={4}
                      disabled={isProcessing || isSubmitting || isVerificationComplete}
                      className={errors.otp ? "p-invalid" : ""}
                    />
                  </RFG>
                  
                  <div className="text-center mb-4">
                    <Button
                      variant="outline-info"
                      size="sm"
                      onClick={handleResendOTP}
                      disabled={resendDisabled || resendOtpState?.loading || isProcessing || isVerificationComplete}
                    >
                      {resendOtpState?.loading 
                        ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-1" />
                            Sending...
                          </>
                        )
                        : resendDisabled 
                          ? `Resend OTP in ${timer}s` 
                          : "Resend OTP"
                      }
                    </Button>
                  </div>

                  {(isProcessing || isSubmitting) && renderLoadingState()}

                  <div className="d-flex justify-content-center gap-3 mt-4">
                    <Button
                      variant="outline-danger"
                      onClick={() => {
                        console.log("🚪 [OTP] User cancelled verification");
                        localStorage.removeItem("temp-data");
                        navigate("/");
                      }}
                      disabled={isProcessing || isSubmitting || isVerificationComplete}
                    >
                      Cancel
                    </Button>
                    <Button 
                      variant="primary"
                      type="submit"
                      disabled={isProcessing || isSubmitting || !customerData || isVerificationComplete}
                    >
                      {isProcessing || isSubmitting ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-1" />
                          Verifying...
                        </>
                      ) : isVerificationComplete ? (
                        <>
                          <Check size={16} className="me-1" />
                          Verified
                        </>
                      ) : (
                        <>
                          <Check size={16} className="me-1" />
                          Verify & Continue
                        </>
                      )}
                    </Button>
                  </div>
                </form>
                
                {renderErrorMessages()}
                
                <div className="text-center mt-4">
                  <small className="text-muted">
                    🔒 Your data is encrypted and secure<br/>
                    OTP is valid for 10 minutes
                  </small>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
      <Footer />
    </>
  );
}
